<template>
  <div class="container-custom moo-sheet-page moo-reward-detail-page" :style="styleObject">
    <div class="content-custom">
      <div
        class="moo-sheet-shell"
        :class="{ 'moo-sheet-shell--with-submit-bar': canRedeem }"
      >
        <MooSheetHead
          icon="redeem"
          :title="$t('rewardCatalogue.detailTitle')"
          @close="goBack()"
        />

        <div v-if="isShowLayout">
          <!-- Voucher summary -->
          <div class="rd-hero">
            <div class="rd-hero__icon">
              <img v-if="reward.icon_url" :src="reward.icon_url" :alt="reward.reward_title" />
              <span class="material-icons-outlined" v-else>redeem</span>
            </div>
            <div class="rd-hero__body">
              <div class="rd-hero__name">{{ reward.reward_title || "" }}</div>
              <div class="rd-hero__valid" v-if="reward.expiry_date_text">
                {{ $t('rewardCatalogue.validFor', { date: reward.expiry_date_text }) }}
              </div>
              <div class="rd-hero__cost">{{ costText }}</div>
            </div>
          </div>

          <!-- Applicability -->
          <template v-if="applicability.length">
            <div class="rd-card">
              <div class="rd-row" v-for="(line, index) in applicability" :key="index">
                <span class="material-icons-round rd-row__check">check_circle</span>
                <span>{{ line }}</span>
              </div>
            </div>
          </template>

          <!-- Terms -->
          <template v-if="termsLines.length">
            <div class="rd-section-title">{{ $t('rewardCatalogue.termsTitle') }}</div>
            <div class="rd-card">
              <div class="rd-row" v-for="(line, index) in termsLines" :key="index">
                <span class="material-icons-round rd-row__check">check_circle</span>
                <span>{{ line }}</span>
              </div>
            </div>
          </template>

          <!-- Where to use -->
          <template v-if="reward.where_to_use">
            <div class="rd-section-title">{{ $t('rewardCatalogue.whereToUse') }}</div>
            <div class="rd-card">
              <div class="rd-row">
                <span class="material-icons-round rd-row__check">place</span>
                <span class="rd-where" v-html="reward.where_to_use"></span>
              </div>
            </div>
          </template>

          <SubmitOrderBar
            v-if="canRedeem"
            :label="$t('rewardCatalogue.redeem')"
            :loading="loaderActive"
            @submit="openRedeemConfirm()"
          />
        </div>
      </div>
    </div>

    <ModalConfirmSheet
      :message="confirmMessage"
      icon="redeem"
      :confirm-text="$t('common.yes')"
      :cancel-text="$t('common.cancel')"
      :process-yes="processExchange"
    >
      <!-- Shown only when the merchant lets this voucher be bought by card;
           otherwise the sheet keeps its plain yes/no shape. -->
      <div class="sheet-methods" v-if="showPayPicker">
        <button
          type="button"
          class="sheet-method"
          :class="{
            'sheet-method--selected': pickedPay === PAY_BALANCE,
            'sheet-method--disabled': reward.is_disabled,
          }"
          :disabled="reward.is_disabled"
          @click="pickedPay = PAY_BALANCE"
        >
          <span class="sheet-method__radio"></span>
          <span class="material-icons-round sheet-method__icon">{{
            isPointMerchant ? "stars" : "account_balance_wallet"
          }}</span>
          <span class="sheet-method__body">
            <span class="sheet-method__label">{{ balanceMethodLabel }}</span>
            <span class="sheet-method__note">
              {{ balanceText }}
              <template v-if="reward.is_disabled">
                — {{ $t('rewardCatalogue.notEnough') }}
              </template>
            </span>
          </span>
        </button>

        <button
          type="button"
          class="sheet-method"
          :class="{
            'sheet-method--selected': pickedPay === PAY_CARD,
            'sheet-method--disabled': reward.is_disabled_by_card,
          }"
          :disabled="reward.is_disabled_by_card"
          @click="pickedPay = PAY_CARD"
        >
          <span class="sheet-method__radio"></span>
          <span class="material-icons-round sheet-method__icon">credit_card</span>
          <span class="sheet-method__body">
            <span class="sheet-method__label">{{ $t('rewardCatalogue.payCard') }}</span>
            <span class="sheet-method__note">{{ $t('rewardCatalogue.payCardNote') }}</span>
          </span>
        </button>
      </div>
    </ModalConfirmSheet>

    <ModalSuccess
      :processAcction="gotoMyVouchers"
      :success="true"
      :message="successMessage"
      :buttonText="$t('rewardCatalogue.goToMyVouchers')"
    />

    <div class="position-fixed top-1 end-1 z-index-2 message-success">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="loaderActive" />
  </div>
</template>

<script>
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import MooSheetHead from "@/components/MooSheetHead.vue";
import ModalConfirmSheet from "@/components/ModalConfirmSheet.vue";
import ModalSuccess from "@/components/ModalSuccess.vue";
import SubmitOrderBar from "@/components/SubmitOrderBar.vue";
import PromocodeService from "../services/promocode.service";
import MemberService from "../services/member.service";
import { STRIPE, AIRWALLEX } from "@/config/const";
import { isGatewayReturn } from "@/config/utils";
import $ from "jquery";

// merchant_setting.reward_per_transaction value meaning "points, not cashback".
const REWARD_POINT = 2;

const PAY_BALANCE = "balance";
const PAY_CARD = "card";

export default {
  name: "reward-detail",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    MooSheetHead,
    ModalConfirmSheet,
    ModalSuccess,
    SubmitOrderBar,
  },
  data() {
    return {
      reward: {},
      merchantMember: {},
      isShowLayout: false,
      loaderActive: false,
      snackbar: null,
      message: "",
      PAY_BALANCE,
      PAY_CARD,
      pickedPay: PAY_BALANCE,
      paymentMode: STRIPE,
      // Read before handleCardReturn() strips the query; goBack() needs it.
      cameFromGateway: false,
      // The issuing job had not reported back by the time the success sheet
      // opened; the receipt says so rather than promising a finished voucher.
      redeemStillProcessing: false,
    };
  },
  async mounted() {
    this.cameFromGateway = isGatewayReturn(this.$route.query);
    this.getDefaultPaymentMode();
    // Verify before loading, the same order RewardCatalogue.vue uses: the
    // gateway return has to be settled first so the detail that renders is the
    // one that already accounts for the voucher just paid for. That path loads
    // the detail itself — hence the guard, so it is not fetched twice.
    if (!(await this.handleCardReturn())) {
      this.getRewardDetail();
    }
  },
  computed: {
    styleObject() {
      // Same Theme 2 base the rest of the reskinned MOO screens use, instead of
      // the legacy moo_color this page used to read.
      return {
        "--color":
          this.$store.state.mainThemeColorTheme2 ||
          this.$store.state.color ||
          "#201a1a",
      };
    },
    isPointMerchant() {
      return this.merchantMember.reward_type == REWARD_POINT;
    },
    costText() {
      return this.isPointMerchant
        ? this.reward.point_text
        : this.reward.amount_text;
    },
    // Open if EITHER method is available: a voucher out of reach on balance can
    // still be bought by card, same rule as the catalogue's canRedeem(item).
    canRedeem() {
      return (
        this.isShowLayout &&
        (!this.reward.is_disabled_btn || !this.reward.is_disabled_by_card)
      );
    },
    showPayPicker() {
      return !!this.reward.id && !this.reward.is_disabled_by_card;
    },
    balanceText() {
      return this.isPointMerchant
        ? this.merchantMember.point_format
        : this.merchantMember.amount_format;
    },
    balanceMethodLabel() {
      return this.isPointMerchant
        ? this.$t("rewardCatalogue.payPoint", { name: this.reward.point_name })
        : this.$t("rewardCatalogue.payWallet");
    },
    // The four "this voucher applies to ..." lines the endpoint returns.
    applicability() {
      return [
        this.reward.tier_can_exchange_text,
        this.reward.branch_can_exchange_text,
        this.reward.item_apply_text,
        this.reward.order_minimum_text,
      ].filter((line) => line);
    },
    termsLines() {
      return (this.reward.terms_arr ?? []).filter((line) => line);
    },
    // Same ps-* receipt body the catalogue's confirm sheet uses, so redeeming
    // from either screen looks like the same action.
    confirmMessage() {
      if (!this.reward || !this.reward.id) {
        return "";
      }
      const rows = [
        {
          icon: this.isPointMerchant ? "stars" : "payments",
          label: this.$t("rewardCatalogue.rowCost"),
          value: this.costText,
        },
        {
          icon: "account_balance_wallet",
          label: this.$t("rewardCatalogue.rowBalance"),
          value: this.isPointMerchant
            ? this.merchantMember.point_format
            : this.merchantMember.amount_format,
        },
      ];

      const rowsHtml = rows
        .map(
          (r) => `
        <div class="ps-row">
          <span class="material-icons-outlined ps-row-icon">${r.icon}</span>
          <span class="ps-row-label">${r.label}</span>
          <span class="ps-row-value">${r.value ?? ""}</span>
        </div>`
        )
        .join("");

      return `
        <span class="ps-title">${this.$t("rewardCatalogue.confirmTitle")}</span>
        <span class="ps-amount">${this.reward.reward_title || ""}</span>
        <span class="ps-rows">${rowsHtml}</span>
      `;
    },
    // Shared with RewardCatalogue's sheet, which renders the same two lines
    // through the same ModalSuccess component.
    successMessage() {
      const note = this.redeemStillProcessing
        ? `<span class="ps-note">${this.$t(
            "rewardCatalogue.redeemStillProcessing"
          )}</span>`
        : "";

      return `
        <span class="ps-title">${this.$t("rewardCatalogue.redeemSuccess")}</span>
        <span class="ps-amount">${this.reward.reward_title || ""}</span>
        ${note}
      `;
    },
  },
  methods: {
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
    goBack() {
      // Same reason as RewardCatalogue.goBack(): after a gateway redirect the
      // previous history entry is the hosted checkout, not a screen of ours.
      if (this.cameFromGateway) {
        this.$router.push({
          name: "RewardCatalogue",
          params: this.$route.params,
        });
        return;
      }
      this.$router.go(-1);
    },
    // The list of vouchers the member now HOLDS, not MyReward — that screen
    // picks a voucher to apply to the order in hand. Same destination the
    // member site sends them to after a redeem.
    gotoMyVouchers() {
      // Also the success sheet's action button; dismiss it before navigating so
      // the modal never outlives the screen and strands its backdrop.
      $("#success-modal .btn-close").click();
      this.$router.push({ name: "MyVouchers", params: this.$route.params });
    },
    async getRewardDetail() {
      this.showLoader();
      const rewardId = this.$route.params.reward_id;

      await PromocodeService.getRewardDetail(rewardId).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.reward = response.data.data.reward;
            this.merchantMember = response.data.data.merchant_member;
            this.isShowLayout = true;
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error;
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
    openRedeemConfirm() {
      // Preselect whichever method is actually usable.
      this.pickedPay = this.reward.is_disabled ? PAY_CARD : PAY_BALANCE;
      $(".btn-confirm-sheet-modal").click();
    },
    getDefaultPaymentMode() {
      MemberService.getDefaultPaymentMode().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.paymentMode =
              parseInt(response.data.data.default_payment_mode) || STRIPE;
          }
        },
        () => {}
      );
    },
    async processExchange() {
      // Both sheet buttons carry data-bs-dismiss; it is already closing.
      const rewardId = this.reward?.id ?? 0;
      if (!rewardId) {
        return;
      }
      if (this.pickedPay === PAY_CARD) {
        this.payByCard();
        return;
      }
      this.closeSnackbar();
      this.showLoader();

      await PromocodeService.exchangeReward({ reward_id: rewardId }).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.getRewardDetail();
            // Granted inline, no queued job to wait on.
            this.redeemStillProcessing = false;
            $(".btn-success-modal").click();
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error;
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
    // Where the gateway sends the member back — this same screen, so the
    // outcome is reported where the redeem was started.
    returnUrl() {
      const merchantCode = this.$store.state.merchantCode;
      const branchCode = this.$store.state.branchCode;
      const rewardId = this.$route.params.reward_id;
      let url = location.protocol + "//" + location.host + "/" + merchantCode;
      if (this.$route.params.table_code) {
        url +=
          "/QR/" + branchCode + "/" + this.$route.params.table_code;
      } else {
        url += "/" + branchCode;
      }
      return url + "/reward-detail/" + rewardId;
    },
    // Card payment takes the same route as TopUp.vue's: the checkout call only
    // creates the hosted session and stamps metadata.isExchangeVoucher on it.
    // Nothing is granted here and nothing is granted by the return URL — the
    // gateway webhook is what queues ProcessWebhookJob, and handleExchangeVoucher
    // inside that job is the only thing that issues the voucher. So closing the
    // tab after paying still gets the member their voucher.
    payByCard() {
      this.closeSnackbar();
      this.showLoader();

      const url = this.returnUrl();
      const dataSend = {
        images: this.reward.icon_url ?? "",
        name: this.reward.reward_title || "",
        unit_amount: this.reward.amount_convert,
        quantity: 1,
        member_id: this.reward.member_id,
        isExchangeVoucher: 1,
        voucher_id: this.reward.id,
        // Marked so the landing page can tell "came back from the gateway"
        // from a normal visit — cancelling leaves the checkout page sitting in
        // history behind us exactly the way paying does.
        cancel_url: url + "?exchange=cancel",
      };

      const isAirwallex = this.paymentMode == AIRWALLEX;
      const request = isAirwallex
        ? MemberService.checkoutAirwallex({
            ...dataSend,
            // The BE appends airwallex_intent_id to this URL itself.
            success_url: url + "?exchange=success",
          })
        : MemberService.checkoutStripe({
            ...dataSend,
            success_url:
              url + "?session_id={CHECKOUT_SESSION_ID}&exchange=success",
          });

      request.then(
        (response) => {
          if (response.data.result.isSuccess) {
            window.location.href = response.data.data.url;
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
            this.hideLoader();
          }
        },
        (error) => {
          this.snackbar = "danger";
          this.message =
            error?.response?.data?.result?.message ?? "Checkout failed";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
    // Backup for a webhook that is slow or never lands: the check endpoint
    // re-dispatches the job when no log exists yet, and reports the job's own
    // error when one ran and failed — so the success sheet is never shown for a
    // payment that granted nothing.
    // Returns true when this WAS a gateway return, so mounted() knows the detail
    // has already been loaded here.
    async handleCardReturn() {
      const query = this.$route.query;
      if (query.exchange !== "success") {
        // Cancelled at the gateway. cameFromGateway has already been read, so
        // the marker has done its job and can go.
        if (query.exchange) {
          this.$router.replace({ path: this.$route.path, query: {} });
        }
        return false;
      }

      if (!query.session_id && !query.airwallex_intent_id) {
        return false;
      }

      this.showLoader();

      // WAIT for the job that actually issues the voucher rather than just
      // verifying the payment: the check endpoint only queues that job, so a
      // single isSuccess means "paid", not "granted". Same wait TopUp.vue does.
      const outcome = await MemberService.pollPaymentJobStatus(
        query.session_id,
        query.airwallex_intent_id
      );

      if (outcome.status === "failed") {
        // Clear the gateway params here too: the stash has already been
        // consumed, so a reload would re-run this against nothing and could end
        // up opening the success sheet with no voucher name in it.
        this.$router.replace({ path: this.$route.path, query: {} });
        this.snackbar = "danger";
        this.message = outcome.message;
        this.setTimeOutNotification();
        await this.getRewardDetail();
        this.hideLoader();
        return true;
      }

      this.$router.replace({ path: this.$route.path, query: {} });
      // Ran out of window rather than finished: the payment is taken and the
      // job is still coming, so the receipt says so.
      this.redeemStillProcessing = outcome.status === "pending";
      // Load BEFORE opening: successMessage reads this.reward, so a sheet
      // opened first would flash an empty receipt and fill in a moment later.
      await this.getRewardDetail();
      $(".btn-success-modal").click();
      this.hideLoader();
      return true;
    },
  },
};
</script>
<style scoped>
/* Shell, tokens, sheet head and the modal chrome come from
   assets/css/moo-sheet.css — this file only styles what is unique here. */
.container-custom {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.content-custom {
  flex: 1;
  overflow: auto;
}

.rd-hero {
  display: flex;
  gap: 14px;
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 16px;
}
.rd-hero__icon {
  width: 76px;
  height: 76px;
  flex-shrink: 0;
  border-radius: 14px;
  background: var(--sheet-08);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.rd-hero__icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.rd-hero__icon .material-icons-outlined {
  font-size: 30px;
  color: var(--sheet);
}
.rd-hero__body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.rd-hero__name {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #241a12;
  word-break: break-word;
}
.rd-hero__valid {
  font-size: 12px;
  color: #857372;
  margin-top: 3px;
}
.rd-hero__cost {
  font-size: 18px;
  font-weight: 800;
  color: var(--sheet);
  margin-top: 8px;
}

.rd-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #241a12;
  margin-bottom: 10px;
}

.rd-card {
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 14px;
  padding: 12px 14px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.rd-row {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 13px;
  line-height: 1.5;
  color: #6b5d55;
}
.rd-row__check {
  font-size: 17px;
  color: var(--sheet);
  flex-shrink: 0;
  margin-top: 1px;
}
.rd-where :deep(p) {
  margin: 0;
}
</style>
